import { Redirect, Route, Switch } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useBaasInstance } from "../../../Contexts/BaasInstanceContext";
import { useBaasPlatformCreateAccess } from "../../../Hooks/useBaasPlatformCreateAccess";
import { isBaasHmlInstance } from "../../../Services/baasInstancesApi";
import { BaasPlaceholder } from "./BaasPlaceholder";
import { BaasConsoleRoute } from "./components/BaasConsoleRoute";
import { BaasHmlEnvBanner } from "./components/BaasHmlEnvBanner";
import { BaasViewAsBanner } from "./components/BaasViewAsControls";
import { readBaasViewAsPersona } from "./baasViewAs";
import { BaasStablesPage } from "./Stables/BaasStablesPage";
import { BaasApiPage } from "./Api/BaasApiPage";
import { BaasInstancesPage } from "./Instances/BaasInstancesPage";
import { BaasCardProgramCreatePage } from "./Processing/BaasCardProgramCreatePage";
import { BaasCardProgramDetailPage } from "./Processing/BaasCardProgramDetailPage";
import { BaasCardAccountsPage } from "./Processing/BaasCardAccountsPage";
import { BaasCardAccountCreatePage } from "./Processing/BaasCardAccountCreatePage";
import { BaasCardAccountDetailPage } from "./Processing/BaasCardAccountDetailPage";
import { BaasCartoesPage } from "./Processing/BaasCartoesPage";
import { BaasCartoesRebatePage } from "./Processing/BaasCartoesRebatePage";
import { BaasCartoesSettlementPage } from "./Processing/BaasCartoesSettlementPage";
import { BaasCartoesIssuerSettingsPage } from "./Processing/BaasCartoesIssuerSettingsPage";
import { BaasProcessingPage } from "./Processing/BaasProcessingPage";
import { BaasKybPage } from "./Kyb/BaasKybPage";
import { BaasUsersPage } from "./Admin/BaasUsersPage";
import { BaasGroupsPage } from "./Admin/BaasGroupsPage";
import { BaasSettingsPage } from "./Admin/BaasSettingsPage";
import { BaasPldPage } from "./Pld/BaasPldPage";
import { BaasProviderConsolePage } from "./Provider/BaasProviderConsolePage";
import { BaasHomologacaoPage } from "./Instances/BaasHomologacaoPage";
import { BaasEntidadeCreatePage } from "./Entidades/BaasEntidadeCreatePage";
import { BaasEntidadeDetailPage } from "./Entidades/BaasEntidadeDetailPage";
import { BaasEntidadePersonalCreatePage } from "./Entidades/BaasEntidadePersonalCreatePage";
import { BaasEntidadesPage } from "./Entidades/BaasEntidadesPage";
import { BaasOnboardPage } from "./Entidades/BaasOnboardPage";
import { BaasTransactionsPage } from "./Transactions/BaasTransactionsPage";
import { BaasStablesWalletPage } from "./Stables/BaasStablesWalletPage";
import { BaasStablesWalletDetailPage } from "./Stables/BaasStablesWalletDetailPage";
import { BaasStablesMetricsPage } from "./Stables/BaasStablesMetricsPage";
import {
  BaasStablesBuyPage,
  BaasStablesDepositPage,
  BaasStablesSellPage,
  BaasStablesSwapPage,
  BaasStablesWithdrawPage,
} from "./Stables/BaasStablesOperationPage";
import {
  BAAS_PLACEHOLDER_ROUTES,
  BAAS_ACCOUNTS_PATH,
  BAAS_VIRTUAL_ACCOUNTS_PATH,
  BAAS_BASE_PATH,
  BAAS_CARTOES_CREDITO_PATH,
  BAAS_CARTOES_ACCOUNTS_PATH,
  BAAS_CARTOES_ACCOUNTS_CREATE_PATH,
  BAAS_CARTOES_REBATE_PATH,
  BAAS_CARTOES_SETTLEMENT_PATH,
  BAAS_CARTOES_SETTINGS_PATH,
  BAAS_API_PATH,
  BAAS_EXTRATO_PATH,
  BAAS_HOMOLOGACAO_PATH,
  BAAS_INSTANCE_CREATE_PATH,
  BAAS_INSTANCE_USERS_PATH,
  BAAS_INSTANCES_PATH,
  BAAS_ONBOARD_PATH,
  BAAS_ORG_USERS_PATH,
  BAAS_PIX_PATH,
  BAAS_PLD_PATH,
  BAAS_PROVIDER_PATH,
  BAAS_ROOT_PATH,
  BAAS_SALDO_PATH,
  BAAS_STABLES_BUY_PATH,
  BAAS_STABLES_DEPOSIT_PATH,
  BAAS_STABLES_METRICS_PATH,
  BAAS_STABLES_SELL_PATH,
  BAAS_STABLES_SWAP_PATH,
  BAAS_STABLES_TRANSFER_PATH,
  BAAS_STABLES_WALLET_PATH,
  BAAS_STABLES_WITHDRAW_PATH,
} from "./constants";
import { BaasAccountsPage } from "./Accounts/BaasAccountsPage";
import { BaasAccountCreatePage } from "./Accounts/BaasAccountCreatePage";
import { BaasAccountDetailPage } from "./Accounts/BaasAccountDetailPage";
import { BaasAccountLimitsConfigPage } from "./Accounts/BaasAccountLimitsConfigPage";
import { BaasVirtualAccountsPage } from "./Accounts/BaasVirtualAccountsPage";
import { BaasRootPage } from "./Root/BaasRootPage";
import { BaasInstanceCreatePage } from "./Instances/BaasInstanceCreatePage";

/**
 * Console chrome: Stripe-like HML sandbox strip (when HML selected) + Valut-admin
 * view-as amber banner. Banners stack sticky; sandbox first, then view-as when
 * both are active. Not shown on Production.
 * Instance filter and "Visualizar como" live in the app header (HeaderActionButtons).
 */
const BaasConsoleChrome = () => {
  const { canUseViewAs } = useBaasPlatformCreateAccess();
  const { selectedInstance } = useBaasInstance();
  const [viewAsTick, setViewAsTick] = useState(0);
  const isHml = isBaasHmlInstance(selectedInstance);
  const viewAsActive = useMemo(
    () => Boolean(readBaasViewAsPersona()),
    [viewAsTick],
  );
  const showBannerStack = (canUseViewAs && viewAsActive) || isHml;

  useEffect(() => {
    const onViewAs = () => setViewAsTick((n) => n + 1);
    window.addEventListener("storage", onViewAs);
    window.addEventListener("baas-view-as-changed", onViewAs);
    return () => {
      window.removeEventListener("storage", onViewAs);
      window.removeEventListener("baas-view-as-changed", onViewAs);
    };
  }, []);

  if (!showBannerStack) return null;

  return (
    <div className="baas-console-chrome mb-5 md:mb-6">
      <div className="sticky top-0 z-30 -mx-4 md:-mx-5 lg:-mx-8">
        {isHml ? <BaasHmlEnvBanner flush /> : null}
        {canUseViewAs && viewAsActive ? <BaasViewAsBanner flush /> : null}
      </div>
    </div>
  );
};

/**
 * BaaS console pages rendered inside PrivateApp.
 * Sidebar navigation is handled by MenuNavigation (always-open nested menu).
 */
const BaasConsoleApp = () => (
  <div className="baas-console min-w-0">
    <BaasConsoleChrome />
    <Switch>
      <Route exact path="/baas">
        <BaasInstancesPage />
      </Route>
      <Route exact path={BAAS_INSTANCES_PATH}>
        <Redirect to={BAAS_BASE_PATH} />
      </Route>
      <Route exact path={BAAS_INSTANCE_CREATE_PATH}>
        <BaasInstanceCreatePage />
      </Route>
      <Route exact path="/baas/kyb">
        <BaasConsoleRoute>
          <BaasKybPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/inicio">
        <BaasConsoleRoute>
          <BaasProcessingPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/cartoes">
        <BaasConsoleRoute>
          <BaasCartoesPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/cartoes/novo">
        <BaasConsoleRoute>
          <BaasCardProgramCreatePage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_CARTOES_SETTINGS_PATH}>
        <BaasConsoleRoute>
          <BaasCartoesIssuerSettingsPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_CARTOES_SETTLEMENT_PATH}>
        <BaasConsoleRoute>
          <BaasCartoesSettlementPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_CARTOES_REBATE_PATH}>
        <BaasConsoleRoute>
          <BaasCartoesRebatePage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_CARTOES_ACCOUNTS_CREATE_PATH}>
        <BaasConsoleRoute>
          <BaasCardAccountCreatePage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_CARTOES_ACCOUNTS_PATH}>
        <BaasConsoleRoute>
          <BaasCardAccountsPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={`${BAAS_CARTOES_ACCOUNTS_PATH}/:issuerAccountId`}>
        <BaasConsoleRoute>
          <BaasCardAccountDetailPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/cartoes/:programId">
        <BaasConsoleRoute>
          <BaasCardProgramDetailPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/credito">
        <Redirect to={BAAS_CARTOES_CREDITO_PATH} />
      </Route>
      <Route exact path={BAAS_STABLES_DEPOSIT_PATH}>
        <BaasConsoleRoute>
          <BaasStablesDepositPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_STABLES_BUY_PATH}>
        <BaasConsoleRoute>
          <BaasStablesBuyPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_STABLES_SELL_PATH}>
        <BaasConsoleRoute>
          <BaasStablesSellPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_STABLES_SWAP_PATH}>
        <BaasConsoleRoute>
          <BaasStablesSwapPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_STABLES_WITHDRAW_PATH}>
        <BaasConsoleRoute>
          <BaasStablesWithdrawPage />
        </BaasConsoleRoute>
      </Route>
      <Route
        exact
        path={BAAS_STABLES_TRANSFER_PATH}
        render={({ location }) => (
          <Redirect
            to={{ pathname: BAAS_STABLES_WITHDRAW_PATH, search: location.search }}
          />
        )}
      />
      <Route exact path={BAAS_STABLES_METRICS_PATH}>
        <BaasConsoleRoute>
          <BaasStablesMetricsPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={`${BAAS_STABLES_WALLET_PATH}/:walletId`}>
        <BaasConsoleRoute>
          <BaasStablesWalletDetailPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_STABLES_WALLET_PATH}>
        <BaasConsoleRoute>
          <BaasStablesWalletPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_API_PATH}>
        <BaasConsoleRoute>
          <BaasApiPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_HOMOLOGACAO_PATH}>
        <BaasConsoleRoute>
          <BaasHomologacaoPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/entidades/nova/pessoal">
        <BaasConsoleRoute>
          <BaasEntidadePersonalCreatePage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/entidades/nova">
        <BaasConsoleRoute>
          <BaasEntidadeCreatePage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/entidades/:entityId">
        <BaasConsoleRoute>
          <BaasEntidadeDetailPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/entidades">
        <BaasConsoleRoute>
          <BaasEntidadesPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_ONBOARD_PATH}>
        <BaasConsoleRoute>
          <BaasOnboardPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/transacoes">
        <BaasConsoleRoute>
          <BaasTransactionsPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_ROOT_PATH}>
        <BaasConsoleRoute>
          <BaasRootPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/contas/nova">
        <BaasConsoleRoute>
          <BaasAccountCreatePage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/contas/:accountId/limites/configurar">
        <BaasConsoleRoute>
          <BaasAccountLimitsConfigPage />
        </BaasConsoleRoute>
      </Route>
      {/* Nested: /baas/contas/:accountId[/limites|/conta] (+ legacy saldo|extrato|pix|metadata|configuracao redirects) */}
      <Route path="/baas/contas/:accountId">
        <BaasConsoleRoute>
          <BaasAccountDetailPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/contas">
        <BaasConsoleRoute>
          <BaasAccountsPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_VIRTUAL_ACCOUNTS_PATH}>
        <BaasConsoleRoute>
          <BaasVirtualAccountsPage />
        </BaasConsoleRoute>
      </Route>
      {/* Soft redirects: Saldo/Extrato/Pix require a selected account */}
      <Route exact path={BAAS_SALDO_PATH}>
        <Redirect to={BAAS_ACCOUNTS_PATH} />
      </Route>
      <Route exact path={BAAS_EXTRATO_PATH}>
        <Redirect to={BAAS_ACCOUNTS_PATH} />
      </Route>
      <Route exact path={BAAS_PIX_PATH}>
        <Redirect to={BAAS_ACCOUNTS_PATH} />
      </Route>
      <Route exact path={BAAS_ORG_USERS_PATH}>
        <BaasConsoleRoute>
          <BaasUsersPage scope="org" />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_INSTANCE_USERS_PATH}>
        <BaasConsoleRoute>
          <BaasUsersPage scope="instance" />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/grupos">
        <BaasConsoleRoute>
          <BaasGroupsPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/passkey">
        <Redirect to="/baas/configuracoes" />
      </Route>
      <Route exact path="/baas/configuracoes">
        <BaasConsoleRoute>
          <BaasSettingsPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_PLD_PATH}>
        <BaasConsoleRoute>
          <BaasPldPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path={BAAS_PROVIDER_PATH}>
        <BaasConsoleRoute>
          <BaasProviderConsolePage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/notificacoes">
        <Redirect to="/baas/configuracoes" />
      </Route>
      <Route exact path="/baas/integracoes">
        <Redirect to={BAAS_API_PATH} />
      </Route>
      <Route exact path="/baas/processamento">
        <Redirect to="/baas/inicio" />
      </Route>
      <Route exact path="/baas/stables">
        <BaasConsoleRoute>
          <BaasStablesPage />
        </BaasConsoleRoute>
      </Route>
      <Route exact path="/baas/crypto/transacoes">
        <Redirect to="/baas/stables" />
      </Route>
      <Route exact path="/baas/crypto/transacoes/:transactionId">
        <Redirect to="/baas/stables" />
      </Route>
      {BAAS_PLACEHOLDER_ROUTES.map((path) => (
        <Route key={path} exact path={path}>
          <BaasConsoleRoute>
            <BaasPlaceholder />
          </BaasConsoleRoute>
        </Route>
      ))}
      <Route path="/baas/*">
        <BaasConsoleRoute>
          <BaasPlaceholder />
        </BaasConsoleRoute>
      </Route>
    </Switch>
  </div>
);

export default BaasConsoleApp;
